import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

/**
 * Get current subscription information with plan bundle details
 * @param {string} uid - User ID
 * @returns {Promise<Object>} Subscription data with bundle info
 */
export const getCurrentSubscriptionInfo = async (uid) => {
  try {
    const userSubscriptionRef = doc(db, 'userSubscriptions', uid);
    const userSubscriptionDoc = await getDoc(userSubscriptionRef);
    
    if (!userSubscriptionDoc.exists()) {
      return null;
    }
    
    const subscriptionData = userSubscriptionDoc.data();
    
    // If we have bundle info stored, return it
    if (subscriptionData.bundleId) {
      return {
        ...subscriptionData,
        hasCompleteBundle: true
      };
    }
    
    // Otherwise, try to match the priceId to a bundle
    if (subscriptionData.priceId) {
      try {
        const matchingBundle = await findBundleByPriceId(subscriptionData.priceId);
        if (matchingBundle) {
          return {
            ...subscriptionData,
            bundleId: matchingBundle.id,
            bundleName: matchingBundle.name,
            lines: matchingBundle.lines,
            period: matchingBundle.period,
            isMessaging: matchingBundle.isMessaging,
            displayPrice: matchingBundle.displayPrice,
            displayPricePerLine: matchingBundle.displayPricePerLine,
            hasCompleteBundle: false // Indicates this was derived, not stored
          };
        }
      } catch (error) {
        console.warn('Failed to find matching bundle for priceId:', subscriptionData.priceId);
      }
    }
    
    return subscriptionData;
  } catch (error) {
    console.error('Error getting current subscription info:', error);
    throw error;
  }
};

/**
 * Find a bundle by its Stripe price ID
 * @param {string} priceId - Stripe price ID
 * @returns {Promise<Object|null>} Matching bundle or null
 */
const findBundleByPriceId = async (priceId) => {
  try {
    const { getAllPlanBundles } = await import('./planBundleUtils');
    const allBundles = await getAllPlanBundles();
    return allBundles.find(bundle => bundle.priceId === priceId) || null;
  } catch (error) {
    console.error('Error finding bundle by price ID:', error);
    return null;
  }
};

/**
 * Check if user can add more phone lines based on current subscription
 * @param {Object} userDoc - User document from userAliases
 * @param {Object} subscriptionInfo - Current subscription info
 * @returns {Object} { canAdd: boolean, currentLines: number, allowedLines: number, reason?: string }
 */
export const checkPhoneLineEligibility = (userDoc, subscriptionInfo) => {
  const currentLines = userDoc?.phoneAliases?.length || 0;
  
  // Get allowed lines from subscription info or userDoc
  let allowedLines = 1; // Default for free users
  
  if (subscriptionInfo?.lines) {
    allowedLines = subscriptionInfo.lines;
  } else if (userDoc?.allowedPhoneLines) {
    allowedLines = parseInt(userDoc.allowedPhoneLines, 10);
  }
  
  const canAdd = currentLines < allowedLines;
  
  return {
    canAdd,
    currentLines,
    allowedLines,
    reason: !canAdd ? `You've reached the maximum number of phone lines (${allowedLines}) for your current plan.` : null
  };
};

/**
 * Handle subscription upgrade process
 * @param {string} uid - User ID
 * @param {Object} newPlanBundle - Selected plan bundle
 * @param {string} paymentMethodId - Optional new payment method
 * @returns {Promise<Object>} Upgrade result
 */
export const upgradeSubscription = async (uid, newPlanBundle, paymentMethodId = null) => {
  try {
    const response = await fetch('/api/upgrade-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        uid,
        newPlanBundle,
        paymentMethodId
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP error! status: ${response.status}`);
    }

    return data;
  } catch (error) {
    console.error('Error upgrading subscription:', error);
    throw error;
  }
};

/**
 * Get user's Stripe customer information
 * @param {string} uid - User ID
 * @returns {Promise<Object|null>} Customer info with payment methods
 */
export const getCustomerInfo = async (uid) => {
  try {
    const userSubscriptionRef = doc(db, 'userSubscriptions', uid);
    const userSubscriptionDoc = await getDoc(userSubscriptionRef);
    
    if (!userSubscriptionDoc.exists()) {
      return null;
    }
    
    const subscriptionData = userSubscriptionDoc.data();
    return {
      customerId: subscriptionData.stripeId,
      hasPaymentMethod: !!subscriptionData.stripeId
    };
  } catch (error) {
    console.error('Error getting customer info:', error);
    return null;
  }
};

/**
 * Format subscription display information
 * @param {Object} subscriptionInfo - Subscription data
 * @returns {Object} Formatted display information
 */
export const formatSubscriptionDisplay = (subscriptionInfo) => {
  if (!subscriptionInfo) {
    return {
      planName: 'Free Plan',
      displayPrice: '$0',
      period: 'Forever',
      lines: 1,
      isMessaging: false
    };
  }
  
  return {
    planName: subscriptionInfo.bundleName || subscriptionInfo.name || 'Unknown Plan',
    displayPrice: subscriptionInfo.displayPrice || '$0',
    period: subscriptionInfo.period || 'Unknown',
    lines: subscriptionInfo.lines || 1,
    isMessaging: subscriptionInfo.isMessaging || false,
    status: subscriptionInfo.subscriptionStatus || 'unknown'
  };
}; 